import * as React from 'react';
import {Component, deps, inject, observer} from '../lib/component';
import {DiscoverPage} from './discover';
import {classes, style} from 'typestyle';
import {important, percent, px, viewWidth} from 'csx';
import PrismicDOM from 'prismic-dom';
import {
  betweenJustified,
  center,
  centerCenter,
  centerJustified,
  end,
  horizontal,
  start,
  vertical,
  verticallySpaced
} from 'csstips';
import {TypeformPopupButton} from '../components/social/typeform-popup-button';
import {PageHeader} from '../components/page-header';
import {TextNoOrphan} from '../components/text-no-orphan';
import {colorBackground, colorBrand, colorSlate,} from '../theme/color';
import {PageHomeCTASeparator} from '../components/page-home-cta-separator';
import {ButtonPill} from '../components/button-pill';
import {constantGoldenRatio, constantHeaderHeight} from '../theme/constant';
import {mediaMobileOnly, mediaTablet} from '../theme/media';
import {fontHeading} from '../theme/font';
import {ResponsiveImageTile} from '../components/responsive-image-tile';
import {SimpleTileGrid} from '../components/tile-grid';
import {gradientDark} from '../theme/gradient';
import {TagTile} from '../components/tag-tile';
import MainWrapper from '../components/main-wrapper';

export interface LearnPageProps {}

const constants = {
  desktopMaxWidth: px(600)
}

@inject(deps) @observer
export class LearnPage extends Component<LearnPageProps> {
  render() {
    const model = this.props.model.page.learn;
    const {preloadImage} = this.props.controller.image;
    const {handleClickLink} = this.props.controller.ui;

    const onSignUp = this.props.model.user.user
      ? null
      : this.handleSignUp;

    if (!model.slug) return (
      <MainWrapper className={LearnPage.styles.container}>
        <div className={LearnPage.styles.typeContainer}>
          <SimpleTileGrid
            className={LearnPage.styles.tileGrid}
            tiles={model.tiles}
            renderTile={(itemClass, tile, index) => (
              <TagTile
                className={itemClass}
                href={LearnPage.makeHref(tile.slug)}
                key={tile.slug + index}
                tag={tile}
                preload={preloadImage}
                onClickLink={handleClickLink}
              />
            )}
          />
        </div>
      </MainWrapper>
    );

    return (
      <MainWrapper>
        {!model.loading && (
          <PageHeader coverImage={model.cover_image}>
            <h1>
              <TextNoOrphan value={model.page_title} />
            </h1>
            <p>
              <TextNoOrphan value={model.page_subtitle} />
            </p>
          </PageHeader>
        )}
        {!model.loading && (
          <div className={LearnPage.styles.content}>
            <div className={LearnPage.styles.sections}>
              {model.sections.map((section, index) => (
                <div key={index.toString()} className={LearnPage.styles.section}>
                  <div>
                    <h3><TextNoOrphan value={section.heading}/></h3>
                    <div style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: PrismicDOM.RichText.asHtml(section.content) }}/>
                  </div>
                  {section.video && (
                    <div className={classes(LearnPage.styles.video, DiscoverPage.styles.video)}>
                      <div dangerouslySetInnerHTML={{ __html: decodeURIComponent(section.video) }} />
                    </div>
                  )}
                  {!section.video && (
                    <div>
                      <ResponsiveImageTile
                        className={LearnPage.styles.sectionImage}
                        identity={section.image}
                        ratio={constantGoldenRatio}
                        preload={preloadImage}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <PageHomeCTASeparator className={LearnPage.styles.ctaSeparator}/>
            <div className={LearnPage.styles.cta}>
              <h3>{model.cta_title}</h3>
              <p>{model.cta_content}</p>
              {!model.typeform_url && onSignUp && (
                <ButtonPill onClick={onSignUp} primary={true}>
                  {model.cta_button}
                </ButtonPill>
              )}
              {model.typeform_url && (
                <TypeformPopupButton typeformUrl={model.typeform_url}>
                  {model.cta_button}
                </TypeformPopupButton>
              )}
            </div>
          </div>
        )}
      </MainWrapper>
    )
  }

  handleSignUp = () => {
    this.props.controller.onboarding.openSignUpModal('/youtube');
  };

  static makeHref = (slug: string) => `/learn/${slug}`;

  static styles = {
    container: style({
      background: gradientDark,
    }),
    typeContainer: style(
      {
        width: percent(100),
        margin: '0 auto',
        maxWidth: px(1280),
      },
      mediaMobileOnly({
        padding: px(16)
      }),
      mediaTablet({
        padding: constantHeaderHeight
      })
    ),
    tileGrid: style({
      background: colorSlate.darken(0.05).toString()
    }),

    content: style({
      padding: '32px 16px 16px'
    }),
    definition: style({
      boxShadow: '5px 5px 15px 0 rgba(0, 0, 0, 0.11)'
    }),
    sections: style(
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(40),
        margin: '0 0 40px 0',
        $nest: {
          '&> div:first-child h2': {
            marginTop: 0
          }
        }
      }),
      mediaTablet({
        ...vertical,
        ...verticallySpaced(60),
        margin: '40px auto',
        maxWidth: px(1280),
      })
    ),
    sectionImage: style(
      mediaTablet({
        width: percent(100),
        maxWidth: constants.desktopMaxWidth
      }),
      mediaMobileOnly({
        width: percent(100),
        maxWidth: percent(100)
      })
    ),
    section: style(
      mediaMobileOnly({
        ...vertical,
        $nest: {
          '&:nth-child(2n - 1) > *:nth-child(2)': {
            marginBottom: px(20)
          },
          '&:nth-child(2n)': {
            flexDirection: 'column-reverse'
          },
          '&:nth-child(2n) > *:nth-child(1)': {
            marginTop: px(20)
          },
          '& h3': {
            color: colorBrand.toString(),
            fontSize: px(30),
            margin: 0,
          },
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40),
            margin: 0,
          },
          '& h4': {
            color: colorSlate.toString(),
            fontSize: px(18),
            margin: 0,
          }
        }
      }),
      mediaTablet({
        ...horizontal,
        ...center,
        $nest: {
          '&:nth-child(2n)': {
            flexDirection: 'row-reverse'
          },
          '&> div': {
            ...vertical,
            ...verticallySpaced(20),
            ...end,
            ...centerJustified,
            flex: 1
          },
          '&> div:last-child': {
            ...start,
          },
          '& h3': {
            color: colorBrand.toString(),
            fontSize: px(30),
            maxWidth: constants.desktopMaxWidth,
            width: percent(100),
            margin: 0,
            padding: '0 40px',
          },
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40),
            maxWidth: constants.desktopMaxWidth,
            width: percent(100),
            margin: 0,
            padding: '0 40px',
          },
          '& h4': {
            color: colorSlate.toString(),
            fontSize: px(18),
            maxWidth: constants.desktopMaxWidth,
            width: percent(100),
            margin: '0 0 20px 0',
            padding: '0 40px',
          },
          '& > div:first-child > div': {
            maxWidth: constants.desktopMaxWidth,
            margin: 0,
            padding: '0 40px',
          }
        }
      }),
    ),
    team: style(
      mediaMobileOnly({
        $nest: {
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40)
          },
          '&> div': {
            ...vertical,
            ...verticallySpaced(40),
            margin: '40px 0'
          }
        }
      }),
      mediaTablet({
        padding: '0 60px',
        maxWidth: px(1280),
        margin: '60px auto',
        $nest: {
          '& h2': {
            color: colorBrand.toString(),
            fontSize: px(40)
          },
          '&> div': {
            ...horizontal,
            ...betweenJustified,
          },
          '&> p': {
            minWidth: px(400),
            width: viewWidth(35)
          }
        }
      })
    ),
    member: style(
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(20),
        ...center,
        flex: 1,
        flexShrink: 0,
        padding: '0 20px',
        $nest: {
          '&> div:first-child': {
            width: 'calc(100vw - 80px)',
            maxWidth: px(220),
            height: 'calc(100vw - 80px)',
            maxHeight: px(220),
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            borderRadius: percent(50)
          },
          '& h5': {
            width: 'calc(100vw - 80px)',
            maxWidth: px(220),
            margin: '0 0 10px 0',
            fontSize: px(18),
          },
          '& p': {
            width: 'calc(100vw - 80px)',
            maxWidth: px(220),
            margin: 0,
            fontSize: px(16)
          }
        }
      }),
      mediaTablet({
        ...vertical,
        ...verticallySpaced(20),
        flex: 1,
        flexShrink: 0,
        $nest: {
          '&> div:first-child': {
            width: viewWidth(20),
            maxWidth: px(220),
            height: viewWidth(20),
            maxHeight: px(220),
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            borderRadius: percent(50)
          },
          '& h5': {
            margin: 0,
            fontSize: px(16),
          },
          '& p': {
            margin: 0,
            fontSize: px(14)
          }
        }
      })
    ),
    quote: style(
      mediaMobileOnly({
        ...centerCenter,
        ...vertical,
        margin: '40px auto',
        $nest: {
          '&> div:nth-child(1)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          },
          '&> div:nth-child(2)': {
            fontFamily: fontHeading,
            fontSize: px(100),
            color: colorBrand.toString(),
            padding: '40px 0 0 0',
            margin: '0 0 -10px 0'
          },
          '&> div:nth-child(3)': {
            fontSize: px(30),
            lineHeight: px(36),
            maxWidth: px(640),
            fontWeight: 300,
            textAlign: 'center'
          },
          '&> div:nth-child(4)': {
            fontFamily: fontHeading,
            fontSize: px(20),
            lineHeight: px(46),
          },
          '&> div:nth-child(5)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          }
        }
      }),
      mediaTablet({
        ...centerCenter,
        ...vertical,
        margin: '40px auto',
        $nest: {
          '&> div:nth-child(1)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          },
          '&> div:nth-child(2)': {
            fontFamily: fontHeading,
            fontSize: px(100),
            color: colorBrand.toString(),
            padding: '40px 0 0 0',
            margin: '0 0 -10px 0'
          },
          '&> div:nth-child(3)': {
            fontSize: px(30),
            lineHeight: px(36),
            maxWidth: px(640),
            fontWeight: 300,
            textAlign: 'center'
          },
          '&> div:nth-child(4)': {
            fontFamily: fontHeading,
            fontSize: px(20),
            lineHeight: px(46),
          },
          '&> div:nth-child(5)': {
            height: px(48),
            borderLeft: `1px solid ${colorBackground.toString()}`
          }
        }
      }),
    ),
    ctaSeparator: style(
      mediaMobileOnly({
        display: 'none'
      })
    ),
    cta: style({
      ...centerCenter,
      ...vertical,
      ...verticallySpaced(30),
      margin: '40px auto 60px',
      $nest: {
        '& h3': {
          color: colorBrand.toString(),
          fontSize: px(40),
          lineHeight: px(46),
          maxWidth: px(400),
          margin: 0,
          textAlign: 'center'
        },
        '& p': {
          margin: 0,
        },
        '& [role="button"]': {
          fontSize: px(20),
          fontWeight: 'bold'
        }
      }
    }),
    video: style({
      width: percent(100)
    }),
    testimonial: style(
      {
        $nest: {
          '& > div:first-child': {
            width: important('100%'),
            maxWidth: important('none'),
            height: important('auto'),
            maxHeight: important('none'),
            flex: 2,
          },
          '& > div:last-child': {
            textAlign: 'center',
            padding: '0 10px',
            flex: 1,
          }
        }
      }
    )
  }
}